import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import Popover from 'common/components/molecules/Popover';
import { Bell } from 'common/images/FaIcons';
import { BELL_PREVIEW_COUNT, ROUTES } from '../../constants/releaseNotes.constants';
import { getReaderNotes, getUnreadCount, markSeen } from '../../actions/releaseNotes.actions';
import { isUnread } from '../../utils/releaseNotes.utils';
import usePopover from '../../hooks/usePopover';
import { StatusDot, TextButton, typeTone } from '../releaseNotes.styled';
import {
  BellButton, UnreadDot, Panel, PanelHeader, AllRead, Row, RowText, RowMeta, DotSlot, UnreadMark, PanelEmpty, PanelFooter
} from './ReleaseNotesBell.styled';

const BellRow = ({ note, unread, onNavigate }) => {
  const { t } = useTranslation();
  const unreadLabel = t('releaseNotes.bell.unreadRow', { defaultValue: 'Unread' });
  return (
    <Row to={ROUTES.note(note.id)} onClick={onNavigate} data-unread={unread}>
      <DotSlot><StatusDot tone={typeTone(note.type)} /></DotSlot>
      <RowText>
        {note.title}
        <RowMeta>{`${note.release_version} · ${moment.utc(note.date).format('MMM D, YYYY')}`}</RowMeta>
      </RowText>
      {unread && <UnreadMark title={unreadLabel} aria-label={unreadLabel} role="img" />}
    </Row>
  );
};

BellRow.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.string,
    release_version: PropTypes.string,
    date: PropTypes.string,
    live_at: PropTypes.string
  }).isRequired,
  unread: PropTypes.bool.isRequired,
  onNavigate: PropTypes.func.isRequired
};

/**
 * The header bell: a red dot while a release note has gone live since the user
 * last looked, and a popover with the newest notes, the unread ones marked.
 * Opening the reader, or "Mark all read", clears the dot and the marks.
 */
const ReleaseNotesBell = ({
  id, notes, unreadCount, seenAt, unreadLoaded, actions
}) => {
  const { t } = useTranslation();
  const {
    open, setAnchor, getAnchor, toggle, close
  } = usePopover();

  // Ask for the count once per session; every bell on the page shares it.
  useEffect(() => {
    if (!unreadLoaded) actions.getUnreadCount();
  }, []);

  // And again whenever the tab is looked at again, so a note that went live
  // meanwhile (at 00:00 UTC, say) lights the dot without a reload.
  useEffect(() => {
    const handleVisible = () => {
      if (!document.hidden) actions.getUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [actions]);

  const handleToggle = useCallback(() => {
    if (!open) actions.getReaderNotes();
    toggle();
  }, [open, actions, toggle]);

  const label = t('releaseNotes.bell.title', { defaultValue: "What's new" });
  const unreadLabel = t('releaseNotes.bell.unread', { defaultValue: "What's new: unread updates" });

  return (
    <React.Fragment>
      <BellButton
        type="button"
        id={id}
        innerRef={setAnchor}
        open={open}
        title={label}
        aria-label={unreadCount > 0 ? unreadLabel : label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <Bell />
        {unreadCount > 0 && <UnreadDot />}
      </BellButton>
      <Popover open={open} getAnchor={getAnchor} onClose={close} align="right" gap={8}>
        <Panel>
          <PanelHeader>
            {label}
            {/* Nothing is claimed until the count is in. */}
            {unreadLoaded && unreadCount > 0 && (
              <TextButton type="button" onClick={actions.markSeen}>
                {t('releaseNotes.bell.markAllRead', { defaultValue: 'Mark all read' })}
              </TextButton>
            )}
            {unreadLoaded && unreadCount === 0 && (
              <AllRead role="status">{t('releaseNotes.bell.allRead', { defaultValue: 'All caught up' })}</AllRead>
            )}
          </PanelHeader>
          {notes.length === 0 && (
            <PanelEmpty>{t('releaseNotes.bell.empty', { defaultValue: 'No release notes yet.' })}</PanelEmpty>
          )}
          {notes.slice(0, BELL_PREVIEW_COUNT).map(note => (
            <BellRow
              key={note.id}
              note={note}
              unread={isUnread(note, { count: unreadCount, seenAt })}
              onNavigate={close}
            />
          ))}
          <PanelFooter to={ROUTES.READER} onClick={close}>
            {t('releaseNotes.bell.viewAll', { defaultValue: 'View all release notes' })}
          </PanelFooter>
        </Panel>
      </Popover>
    </React.Fragment>
  );
};

ReleaseNotesBell.propTypes = {
  id: PropTypes.string,
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
  unreadCount: PropTypes.number.isRequired,
  seenAt: PropTypes.string,
  unreadLoaded: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    getReaderNotes: PropTypes.func,
    getUnreadCount: PropTypes.func,
    markSeen: PropTypes.func
  }).isRequired
};

ReleaseNotesBell.defaultProps = {
  id: undefined,
  seenAt: null
};

const mapStateToProps = state => ({
  notes: state.releaseNotes.reader.items,
  unreadCount: state.releaseNotes.unread.count,
  seenAt: state.releaseNotes.unread.seenAt,
  unreadLoaded: state.releaseNotes.unread.loaded
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ getReaderNotes, getUnreadCount, markSeen }, dispatch)
});

// The unconnected component, for tests.
export { ReleaseNotesBell as ReleaseNotesBellView };

export default connect(mapStateToProps, mapDispatchToProps)(ReleaseNotesBell);
